import React from "react";
import { Check } from "lucide-react";

/**
 * RosterPicker — the coach's student multi-select.
 *
 * Extracted from Calendar.jsx so the group-lesson page can add students to an
 * ALREADY-CREATED lesson with the exact same control the create form uses.
 * `mode="private"` changes the copy for 1-on-1 / semi-private lessons.
 */
export default function RosterPicker({ students = [], selectedIds = [], onChange, mode = "group", title }) {
  const toggle = (id) => {
    if (selectedIds.includes(id)) onChange(selectedIds.filter((x) => x !== id));
    else onChange([...selectedIds, id]);
  };
  const isPrivate = mode === "private";
  const headerLabel = title || (isPrivate ? "Student(s)" : "Pre-assign students");
  const hint = isPrivate
    ? "Leave empty for an open slot · pick 1 for a 1-on-1 · pick 2+ for a semi-private lesson (siblings, friends, a family)"
    : "Optional — hand-pick a small group";

  return (
    <div data-testid="roster-picker">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">
          {headerLabel} <span className="text-white/50 normal-case tracking-normal text-[11px]">({hint})</span>
        </label>
        {selectedIds.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-[10px] text-[#38BDF8] hover:underline"
            data-testid="roster-picker-clear"
          >
            Clear ({selectedIds.length})
          </button>
        )}
      </div>
      {students.length === 0 ? (
        <div className="text-[11px] text-[#A3A3A3] border border-dashed border-white/10 p-3">
          No students on your roster yet. Invite students first, then hand-pick them here.
        </div>
      ) : (
        <div className="border border-white/10 max-h-48 overflow-y-auto divide-y divide-white/5">
          {students.map((s) => {
            const active = selectedIds.includes(s.user_id);
            return (
              <button
                key={s.user_id}
                type="button"
                onClick={() => toggle(s.user_id)}
                className={`w-full text-left flex items-center justify-between gap-3 px-3 py-2 transition-colors ${active ? "bg-[#38BDF8]/10" : "hover:bg-white/5"}`}
                data-testid={`roster-pick-${s.user_id}`}
              >
                <div className="min-w-0">
                  <div className={`text-sm font-bold truncate ${active ? "text-[#38BDF8]" : "text-white"}`}>{s.name}</div>
                  <div className="text-[10px] text-[#A3A3A3] uppercase tracking-wider">
                    DUPR {s.skill_level || "—"}
                    {s.goals ? ` · ${String(s.goals).slice(0, 40)}${String(s.goals).length > 40 ? "…" : ""}` : ""}
                  </div>
                </div>
                <div className={`w-4 h-4 border ${active ? "bg-[#38BDF8] border-[#38BDF8]" : "border-white/30"} grid place-items-center shrink-0`}>
                  {active && <Check size={10} className="text-black" />}
                </div>
              </button>
            );
          })}
        </div>
      )}
      {selectedIds.length > 0 && (
        <div className="mt-2 text-[11px] text-[#38BDF8]" data-testid="roster-picker-summary">
          {selectedIds.length === 1
            ? (isPrivate ? "1-on-1 with this student. They'll be notified when you save." : "1 student will be notified.")
            : `${selectedIds.length} students will be notified when you save.`}
        </div>
      )}
    </div>
  );
}
